import { FilterValueType, instanceOfRangeValue, instanceOfInputValue } from 'festo-react-ui-kit';
import { FilterBase, ResultFilterValue, PopUpRequestObject } from '../../interfaces';
import { TypeUseTranslationHook } from '../../features/translation/hooks/useTranslation';
import { AllFilters } from '../../features/filter/interfaces';

export const getFacetValue = (
  value: FilterValueType | FilterValueType[],
  required?: boolean,
  resetOnEmpty?: boolean,
): FilterValueType | FilterValueType[] => {
  let result = value;

  if (instanceOfRangeValue(result)) {
    result = { ...result, textInput: true };
  } else if (instanceOfInputValue(result)) {
    result = {
      ...result,
      placeholder: !required ? '' : undefined,
      resetOnEmpty: required && resetOnEmpty,
    };
  }

  return result;
};

// Filter modification depending on filter.key and updating the value with redux store which contains the latest data
// After that we return the new object
export const resultFilterPreparation = (
  filter: FilterBase,
  storedValue: ResultFilterValue,
  t: TypeUseTranslationHook,
) => {
  switch (filter.key) {
    case 'recommendedResults':
      if (filter.filters === undefined) return filter;
      const { value: updatedRecommendedResultsValue } = filter.filters[0];

      const modelRecommendedResultsModified =
        Array.isArray(updatedRecommendedResultsValue) &&
        updatedRecommendedResultsValue.map((item) => {
          return {
            ...item,
            label: t('[filterFestoRecommendedResult]'),
            selected: storedValue,
          };
        });

      const childrenRecommendedResults = filter.filters.map((child) => {
        return {
          ...child,
          value: modelRecommendedResultsModified,
        };
      });
      return { ...filter, filters: childrenRecommendedResults };
    case 'fixedStop':
      if (filter.filters === undefined) return filter;
      const { value: updatedValue } = filter.filters[0];

      const modelModified =
        Array.isArray(updatedValue) &&
        updatedValue.map((item) => {
          return {
            ...item,
            label: t('[filterFixedStop]'),
            selected: storedValue,
          };
        });

      const children = filter.filters.map((child) => {
        return {
          ...child,
          value: modelModified,
        };
      });
      return { ...filter, filters: children };

    case 'additionalFunction':
      if (filter.filters === undefined) return filter;
      const { value: batteryProdValue } = filter.filters[0];

      const modelFilterBatteryModified =
        Array.isArray(batteryProdValue) &&
        batteryProdValue.map((item) => {
          return {
            ...item,
            label: t('[filterForBatteryProduction]'),
            selected: storedValue ?? false,
          };
        });

      const addFuncChildren = filter.filters.map((child) => {
        return {
          ...child,
          value: modelFilterBatteryModified,
        };
      });

      return { ...filter, filters: addFuncChildren };

    case 'mountingType':
      const modelFilterMountingTypeModified =
        Array.isArray(filter.value) &&
        filter.value.map((item) => {
          return {
            ...item,
            label:
              item.data === 'LOCK_NUT' ? t('[filterMountingTypeLockNut]') : t('[filterMountingTypeThreadedSleeve]'),
            selected: item.data === storedValue,
          };
        });

      return { ...filter, value: modelFilterMountingTypeModified };

    case 'cushioningAdjustments':
      if (!Array.isArray(storedValue)) return filter;

      const [automatic, manual] = storedValue;
      const modelFilterCushioningAdjustmentModified =
        Array.isArray(filter.value) &&
        filter.value.map((item) => {
          return {
            ...item,
            label:
              item.data === 'AUTOMATIC'
                ? t('[filterCushioningAdjustmentAutomatic]')
                : t('[filterCushioningAdjustmentManual]'),
            selected: automatic === item.data || manual === item.data,
          };
        });

      return { ...filter, value: modelFilterCushioningAdjustmentModified };
    case 'cushioningTechnologies':
      if (!Array.isArray(storedValue)) return filter;
      const [hydraulic, mechanical] = storedValue;
      const modelFilterCategoriesModified =
        Array.isArray(filter.value) &&
        filter.value.map((item) => {
          return {
            ...item,
            label:
              item.data === 'HYDRAULIC'
                ? t('[filterCushioningTechnologyHydraulic]')
                : t('[filterCushioningTechnologyMechanical]'),
            selected: hydraulic === item.data || mechanical === item.data,
          };
        });

      return { ...filter, value: modelFilterCategoriesModified };
    default:
      return filter;
  }
};

type CushioningReactionValue = boolean | null;

interface PreparedCushioningReaction {
  storedSoftValue: CushioningReactionValue;
  storedRobustValue: CushioningReactionValue;
  filter: FilterBase;
  t: TypeUseTranslationHook;
}

export const cushioningReactionPreparation = ({
  storedSoftValue,
  storedRobustValue,
  filter,
  t,
}: PreparedCushioningReaction) => {
  if (filter.filters === undefined) return filter;

  const { value: softModelValue } = filter.filters[0];
  const { value: robustModelValue } = filter.filters[1];

  if (!Array.isArray(softModelValue) || !Array.isArray(robustModelValue)) return filter;

  const modelSoftModified = softModelValue.map((item) => {
    return {
      ...item,
      label: t('[filterCushioningReactionSoft]'),
      selected: storedSoftValue !== null ? true : false,
    };
  });
  const modelRobustModified = robustModelValue.map((item) => {
    return {
      ...item,
      label: t('[filterCushioningReactionRobust]'),
      selected: storedRobustValue !== null ? true : false,
    };
  });

  const children = filter.filters.map((child, index) => {
    return {
      ...child,
      value: index === 0 ? modelSoftModified : modelRobustModified,
    };
  });

  const newFilter: FilterBase = { ...filter, filters: children };

  return newFilter;
};

export const isPopupWithCheckbox = (filter: FilterBase): boolean => {
  return (
    filter.filterDisplayMode.type === 'POP_UP_CALCULATION_COEFFICIENT_OF_FRICTION' &&
    filter.key === 'conditionLubricationCheckBox'
  );
};

enum PopUpFilterKeys {
  CONDITION = 'condition',
  SAFETY_FACTOR = 'safetyFactor',
  PISTON_DIAMETER = 'pistonDiameter',
  MATERIAL_ONE = 'materialOne',
  MATERIAL_TWO = 'materialTwo',
  PRESSURE = 'pressure',
  TIME = 'time',
  ACCELERATION_FACTOR = 'accelerationFactor',
  DISTANCE = 'distance',
  STROKE = 'stroke',
  START_VELOCITY = 'startVelocity',
  LENGTH_OF_LEVER = 'lengthOfLever',
  ANGLE_BETWEEN_LEVER = 'angleBetweenLeverAndShockAbsorber',
  SWING_ANGLE = 'swingAngle',
}

export const popupFilterPreparation = (
  filter: FilterBase,
  t: TypeUseTranslationHook,
  storedValue: number | string | boolean,
) => {
  switch (filter.key) {
    case PopUpFilterKeys.CONDITION:
      if (filter.filters === undefined) return filter;

      const { value: updatedValue } = filter.filters[0];

      const modelModified =
        Array.isArray(updatedValue) &&
        updatedValue.map((item) => {
          return {
            ...item,
            data: 'condition',
            label: t('[filterConditionLubrication]'), //TIP: maybe you need to chance the translation key
            selected: storedValue,
          };
        });

      const children = filter.filters.map((child) => {
        return {
          ...child,
          value: modelModified,
        };
      });

      return { ...filter, filters: children } as FilterBase;
    case PopUpFilterKeys.SAFETY_FACTOR:
      if (!filter.value || !Array.isArray(filter.value)) return filter;

      const savedSafetyFactorModalSegment: FilterValueType[] = filter.value.map((modalSegment) => {
        return {
          ...modalSegment,
          selected: `${storedValue}` === modalSegment.data,
        };
      });
      const preparedFilter: FilterBase = {
        ...filter,
        value: savedSafetyFactorModalSegment,
      };

      return preparedFilter;
    case PopUpFilterKeys.PISTON_DIAMETER:
      if (!filter.value || !Array.isArray(filter.value)) return filter;

      const savedPistonModalSegment: FilterValueType[] = filter.value.map((modalSegment) => {
        return {
          ...modalSegment,
          selected: `${storedValue}` === modalSegment.data,
        };
      });

      const preparedPistonFilter: FilterBase = {
        ...filter,
        value: savedPistonModalSegment,
      };

      return preparedPistonFilter;
    case PopUpFilterKeys.MATERIAL_ONE:
    case PopUpFilterKeys.MATERIAL_TWO:
      if (!filter.value || !Array.isArray(filter.value)) return filter;

      const savedMaterialOneSegment: FilterValueType[] = filter.value.map((modalSegment) => {
        return {
          ...modalSegment,
          selected: storedValue === modalSegment.data,
        };
      });

      const preparedMaterialOneFilter: FilterBase = {
        ...filter,
        value: savedMaterialOneSegment,
      };

      return preparedMaterialOneFilter;
    case PopUpFilterKeys.PRESSURE:
    case PopUpFilterKeys.SWING_ANGLE:
    case PopUpFilterKeys.ANGLE_BETWEEN_LEVER:
    case PopUpFilterKeys.TIME:
    case PopUpFilterKeys.ACCELERATION_FACTOR:
    case PopUpFilterKeys.LENGTH_OF_LEVER:
    case PopUpFilterKeys.DISTANCE:
      if ('value' in filter) {
        const updatedValue = {
          ...filter.value,
          data: storedValue + '',
        } as FilterValueType;

        filter = { ...filter, value: updatedValue };
      }

      return filter;
    case PopUpFilterKeys.START_VELOCITY:
    case PopUpFilterKeys.STROKE:
      if ('value' in filter) {
        const updatedValue = {
          ...filter.value,
          data: storedValue,
        } as FilterValueType;

        filter = { ...filter, value: updatedValue };
      }

      return filter;
    default:
      return filter;
  }
};

export const extractPopupValue = ({
  value,
}: {
  value: FilterValueType | FilterValueType[];
}): number | string | boolean => {
  if (Array.isArray(value)) {
    let valueInsideArray: number | string = 1;

    if (value[0].data === 'conditionLubrication') {
      return value[0].selected;
    }

    value.forEach((vl) => {
      if (vl.selected) {
        valueInsideArray = Number.isInteger(+vl.data) ? +vl.data : vl.data;
      }
    });

    return valueInsideArray;
  }

  const singleValue = Number.isInteger(+value.data) ? +value.data : value.data;
  return singleValue;
};

export const preparePopUpRequest = (popupType: string, filters: AllFilters): PopUpRequestObject | void => {
  switch (popupType) {
    case 'POP_UP_CALCULATION_COEFFICIENT_OF_FRICTION':
      const { materialOne, materialTwo, condition } = filters;
      if (!materialOne || !materialTwo || typeof condition !== 'boolean') break;
      return {
        materialOne: materialOne,
        materialTwo: materialTwo,
        lubrication: condition,
      };
    case 'POP_UP_CALCULATION_FORCE':
      const { pistonDiameter, pressure } = filters;
      if (!pressure || !pistonDiameter) break;

      return {
        pistonDiameter: pistonDiameter,
        pressure: pressure,
      };
    case 'POP_UP_CALCULATION_IMPACT_VELOCITY_ONE':
      const { stroke, time: timeVelocityOne } = filters;
      if (!stroke || !timeVelocityOne) break;

      return {
        stroke: stroke,
        time: timeVelocityOne,
      };
    case 'POP_UP_CALCULATION_IMPACT_VELOCITY_TWO':
      const { distance, time: timeVelocityTwo, accelerationFactor } = filters;
      if (!distance || !timeVelocityTwo || !accelerationFactor) break;
      return {
        distance: distance,
        time: timeVelocityTwo,
        accelerationFactor: accelerationFactor,
      };
    case 'POP_UP_CALCULATION_OF_ANGULAR_VELOCITY':
      let safetyFactorNum = 1;
      const { safetyFactor: safetyFactorAngularVelocity, time: timeAngularVelocity, swingAngle } = filters;
      if (!swingAngle || !timeAngularVelocity) break;

      // Cast safetyFactor from string to number
      if (safetyFactorAngularVelocity) {
        safetyFactorNum = +safetyFactorAngularVelocity;
      }

      return {
        swingAngle: swingAngle,
        time: timeAngularVelocity,
        safetyFactor: safetyFactorNum,
      };
    case 'POP_UP_CALCULATION_OF_START_ANGULAR_VELOCITY':
      const { lengthOfLever, startVelocity, safetyFactor: safetyFactorStartAngularVelocity } = filters;
      if (!lengthOfLever || !safetyFactorStartAngularVelocity) break;

      return {
        startVelocity: startVelocity,
        leverLength: lengthOfLever,
        safetyFactor: safetyFactorStartAngularVelocity,
      };
    case 'POP_UP_CALCULATION_OF_DROP_HEIGHT':
      const { angleBetweenLeverAndShockAbsorber, distanceToCenterOfMass } = filters;
      if (!angleBetweenLeverAndShockAbsorber) break;

      return {
        distanceToCenterOfMass: distanceToCenterOfMass,
        angleBetweenLeverAndShockAbsorber: angleBetweenLeverAndShockAbsorber,
      };
    case 'POP_UP_CALCULATION_SPEED':
      return {
        dropHeight: filters.dropHeight,
      };
  }
};
